import React from 'react';
import { Card, ButtonGroup, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { formatEuroForLanguage } from '../../../utils/i18nHelpers';
import { UNCATEGORIZED } from './expenseListUtils';

// Same colours as the Dashboard "Orders by Status" donut (grey, cyan, blue, green, red) followed by more Bootstrap colours.
export const SLICE_COLORS = ['#0d6efd', '#198754', '#0dcaf0', '#fd7e14', '#6f42c1', '#dc3545', '#20c997', '#d63384', '#ffc107', '#0a58ca', '#146c43', '#6610f2'];
export const UNCATEGORIZED_COLOR = '#adb5bd';
export const OTHER_COLOR = '#6c757d';

const percent = (share, lang) => `${Math.round(share * 100).toLocaleString(lang === 'nl' ? 'nl-NL' : 'en-GB')}%`;

// Donut + legend of spending per category for exactly the visible expenses. Clicking a slice or legend row toggles that category in the filter.
const ExpenseCategorySummary = ({ summary, colorOf, basis, onBasisChange, selectedKeys, onToggleKey, open, onToggleOpen, narrow }) => {
    const { t, i18n } = useTranslation();
    const lang = (i18n?.language || 'en').startsWith('nl') ? 'nl' : 'en';
    const { slices, totalCents } = summary;
    const total = formatEuroForLanguage(totalCents / 100);
    const fill = (s) => (s.key === '__other__' ? OTHER_COLOR : s.key === UNCATEGORIZED ? UNCATEGORIZED_COLOR : colorOf(s.key));
    const keysOf = (s) => (s.members ? s.members.map((m) => m.key) : [s.key]);
    const clickSlice = (s) => onToggleKey(keysOf(s));
    const isSelected = (s) => keysOf(s).every((k) => selectedKeys.includes(k));

    return (
        <Card className="border-0 shadow-sm mb-3 expense-summary" data-testid="expense-summary">
            <Card.Header className="bg-white border-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                    <h6 className="mb-0">{t('expenses.summary.title')}</h6>
                    {narrow && !open && <strong data-testid="expense-summary-total-collapsed">{total}</strong>}
                </div>
                <div className="d-flex align-items-center gap-2">
                    <ButtonGroup size="sm" aria-label={t('expenses.summary.basis')}>
                        <Button variant={basis === 'incl' ? 'primary' : 'outline-primary'} aria-pressed={basis === 'incl'} onClick={() => onBasisChange('incl')}>{t('expenses.summary.incl')}</Button>
                        <Button variant={basis === 'excl' ? 'primary' : 'outline-primary'} aria-pressed={basis === 'excl'} onClick={() => onBasisChange('excl')}>{t('expenses.summary.excl')}</Button>
                    </ButtonGroup>
                    {narrow && (
                        <Button size="sm" variant="light" onClick={onToggleOpen} aria-expanded={open} data-testid="expense-summary-toggle">
                            <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} /> <span className="visually-hidden">{t('expenses.summary.toggle')}</span>
                        </Button>
                    )}
                </div>
            </Card.Header>
            {(!narrow || open) && (
                <Card.Body className="pt-0">
                    {slices.length === 0 ? (
                        <div className="text-center py-4 text-muted" data-testid="expense-summary-empty">
                            <i className="fa-solid fa-chart-pie fa-2x mb-2 opacity-50" />
                            <p className="mb-0">{t('expenses.summary.empty')}</p>
                        </div>
                    ) : (
                        <div className="expense-summary-body">
                            <div className="expense-summary-chart">
                                {typeof window !== 'undefined' && typeof window.ResizeObserver === 'function' && (
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie data={slices} dataKey="cents" nameKey="label" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}
                                            onClick={(_, i) => clickSlice(slices[i])} cursor="pointer">
                                            {slices.map((s) => <Cell key={s.key} fill={fill(s)} stroke={isSelected(s) ? '#212529' : undefined} />)}
                                        </Pie>
                                        <Tooltip formatter={(v, name) => [formatEuroForLanguage(v / 100), name]} />
                                    </PieChart>
                                </ResponsiveContainer>
                                )}
                                <div className="expense-summary-center" aria-hidden="true">
                                    <small className="text-muted">{t('expenses.summary.total')}</small>
                                    <strong data-testid="expense-summary-total">{total}</strong>
                                </div>
                            </div>
                            <ul className="expense-summary-legend list-unstyled mb-0" data-testid="expense-summary-legend">
                                {slices.map((s) => (
                                    <li key={s.key}>
                                        <button type="button" className={`expense-summary-row${isSelected(s) ? ' is-selected' : ''}`} aria-pressed={isSelected(s)}
                                            onClick={() => clickSlice(s)} data-testid={`expense-summary-row-${s.key}`}>
                                            <span className="expense-summary-dot" style={{ background: fill(s) }} />
                                            <span className="expense-summary-label">{s.label}: {percent(s.share, lang)} · {formatEuroForLanguage(s.cents / 100)}</span>
                                        </button>
                                        {s.members && <div className="small text-muted ps-4">{t('expenses.summary.otherContains', { list: s.members.map((m) => m.label).join(', ') })}</div>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card.Body>
            )}
        </Card>
    );
};

export default ExpenseCategorySummary;
